import { SignalGraphD3 } from './signal-graph.d3';
import { type SafeAny, EventType } from '@/common/types';
import { utcFormat } from 'd3';
import { type GraphPoint } from '@/common/ui';
import { i18n, isBadDataValueBySignal } from '@/common/utils';
import { buildEventTooltipContent } from '@/core/helpers';

export class Spo2SignalGraphD3 extends SignalGraphD3 {
  // Static snapshot retained for tooltip code paths or tests that may set it
  // directly. Prefer `spo2DataLookup` — it walks the actual cached epochs the
  // hovered event spans, so the drop value is correct even when the event
  // extends past the visible window or when paintChart's snapshot epoch
  // diverges from the truly displayed epoch (e.g., during wheel-cross
  // overshoot, where `Math.floor(effectiveEpoch)` differs from the
  // base-epoch the chart is actually rendering).
  private extendedData: GraphPoint<SafeAny>[] = [];
  private spo2DataLookup: ((startSec: number, endSec: number) => GraphPoint<SafeAny>[]) | null =
    null;

  setExtendedData(data: GraphPoint<SafeAny>[]): void {
    this.extendedData = data;
  }

  setSpo2DataLookup(
    lookup: ((startSec: number, endSec: number) => GraphPoint<SafeAny>[]) | null
  ): void {
    this.spo2DataLookup = lookup;
  }

  protected updateLine(color: string): this {
    super.updateLine(color);

    this.updateSpO2Labels();
    return this;
  }

  protected buildTooltipContent(signalData: GraphPoint<SafeAny>): string {
    const { t } = i18n.global;

    let yData;

    if (signalData.y.length > 1) {
      yData = (signalData.y[0] + signalData.y[1]) / 2;
    } else {
      yData = signalData.y[0];
    }

    let tooltipHtml = `
     <p><span class='text-capitalize'>${t('time')}</span>: <strong>${utcFormat('%H:%M:%S')(new Date(signalData.x * 1000))}</strong></p>
        <p><span>${this.title}</span>: <strong>${yData ? `${yData}${this.mapping.unit}` : 'Artifact'}</strong></p>
    `;

    if (this.activeEvent) {
      tooltipHtml += buildEventTooltipContent(this.activeEvent);

      if (this.activeEvent.eventType === EventType.DESATURATION) {
        const eventStartSec = this.getEventStartSec(this.activeEvent);
        const eventEndSec = eventStartSec + (this.activeEvent.duration ?? 0);

        tooltipHtml += this.buildDesaturationDropHtml(eventStartSec, eventEndSec);
      }
    }

    return tooltipHtml;
  }

  protected buildLiveDragTooltipContent(
    startSec: number,
    durationSec: number,
    eventType?: string
  ): string {
    let html = super.buildLiveDragTooltipContent(startSec, durationSec, eventType);

    if (eventType === EventType.DESATURATION || eventType === undefined) {
      html += this.buildDesaturationDropHtml(startSec, startSec + durationSec);
    }

    return html;
  }

  private buildDesaturationDropHtml(eventStartSec: number, eventEndSec: number): string {
    const { t } = i18n.global;

    if (!Number.isFinite(eventStartSec) || !Number.isFinite(eventEndSec)) return '';

    const points = this.spo2DataLookup
      ? this.spo2DataLookup(eventStartSec, eventEndSec)
      : this.extendedData;

    const values: number[] = [];
    for (const p of points) {
      if (p.x < eventStartSec || p.x > eventEndSec) continue;
      const py = p.y;
      if (py == null) continue;
      if (Array.isArray(py)) {
        for (const v of py) {
          if (typeof v === 'number' && v > 0 && !isBadDataValueBySignal('SpO2', v)) {
            values.push(v);
          }
        }
      } else if (typeof py === 'number' && py > 0 && !isBadDataValueBySignal('SpO2', py)) {
        values.push(py);
      }
    }

    if (!values.length) return '';

    const drop = Math.max(...values) - Math.min(...values);
    return `<p><span>${t('desaturationDrop')}:</span> <strong>${drop.toFixed(0)}%</strong></p>`;
  }

  private updateSpO2Labels() {
    if (!this.yScale) return;

    let data = this.data;
    data = this.getListOfLabels(data, 15);
    const { min, max } = this.getMinAndMax();
    const averageY = (min + max) / 2;
    const labels = this.graphGroup.selectAll('text.label').data(data);
    labels
      .enter()
      .append('text')
      .classed('label', true)
      .attr('x', (d) => this.xScale(d.x))
      .attr('y', this.yScale(averageY))
      .merge(labels as SafeAny)
      .attr('x', (d) => this.xScale(d.x))
      .attr('y', (d) => this.clampToPlotBounds(this.yScale(d.y)))
      .attr('transform', (d) => {
        let offsetY = d.y < max ? -5 : 11;

        if (this.duration === 1800) {
          offsetY -= 10;
        }

        return `translate(0, ${offsetY})`;
      })
      .attr('text-anchor', 'middle')
      .text((d) => (d.y.length > 1 ? (d.y[0] + d.y[1]) / 2 : d.y));
    labels.exit().remove();
  }
}
